// V2 Design Tokens - shared across Homepage V2 components
export const V2_COLORS = {
  primary: '#185C65',
  primaryForeground: '#FDFDFC',
  primaryLight: '#2a6a73',
  accent: '#01D1B8',
  accentForeground: '#FFFFFF',
  accentOrange: '#E06004',
  accentOrangeAlt: '#E16005',
  accentOrangeHover: '#C85504',
  background: '#FDFDFC',
  backgroundAlt: '#FBF8F3',
  foreground: '#020E0B',
  muted: '#F7F5F3',
  mutedForeground: '#577275',
  mutedLight: '#8AA8A2',
  white: '#FFFFFF',
  border: 'rgba(255, 255, 255, 0.2)',
  borderWhite30: 'rgba(255, 255, 255, 0.3)',
  borderMuted: '#DCE4E5',
  cardBorder: 'rgba(1, 208, 184, 0.5)',
  cardBorderHover: '#01D1B8',
  ring: '#01D1B8',
  tickerUp: '#1FAD66',
  tickerDown: '#D92626',
  logoWall: '#1E3E39',
  premiumGold: '#E8BA30',
  card: '#FCFAF8',
  cardForeground: '#020E0B',
  mutedBg: 'rgba(247, 245, 243, 0.3)',
  destructive: '#EF4343',
  success: '#1DAF8B',
  warning: '#F59F0A',
  locked: '#8F96A3',
  emerald: '#10B981',
  emeraldLight: 'rgba(16, 185, 129, 0.1)',
  emeraldBorder: 'rgba(16, 185, 129, 0.2)',
  emeraldDark: '#059669',
  amber: '#F59F0A',
  amberLight: 'rgba(245, 158, 11, 0.1)',
  amberBorder: 'rgba(245, 158, 11, 0.2)',
  amberDark: '#D97706',
  blue: '#3B82F6',
  blueLight: 'rgba(59, 130, 246, 0.05)',
  blueBorder: 'rgba(59, 130, 246, 0.1)',
  red: '#EF4444',
  redLight: 'rgba(239, 68, 68, 0.1)',
} as const;

export const V2_GRADIENTS = {
  heroBackground: 'linear-gradient(to bottom, #185C65 0%, #185C65 40%, #FCF9F5 100%)',
  heroBackgroundMobile: 'linear-gradient(to bottom, #185C65 0%, #185C65 40%, #FCF9F5 100%)',
  cardHover: 'linear-gradient(to bottom, rgba(1, 209, 184, 0.1), transparent)',
  sectionFade:
    'linear-gradient(to bottom, rgba(252, 249, 245, 1) 0%, rgba(24, 92, 101, 0.08) 100%)',
  sectionFadeAlt:
    'linear-gradient(to bottom, rgba(252, 249, 245, 0.6) 0%, rgba(24, 92, 101, 0.05) 100%)',
  logoWallFadeLeft: 'linear-gradient(to right, #1E3E39, transparent)',
  logoWallFadeRight: 'linear-gradient(to left, #1E3E39, transparent)',
  investorBanner:
    'linear-gradient(to right, rgba(24, 92, 101, 0.05), rgba(1, 209, 184, 0.05), rgba(24, 92, 101, 0.05))',
} as const;

export const V2_TYPOGRAPHY = {
  fontDisplay: "'owners-wide', 'Owners Wide', system-ui, sans-serif",
  fontBody: "'owners-text', 'Owners Text', system-ui, sans-serif",
  h1: {
    mobile: {
      fontSize: '2.25rem',
      lineHeight: 1.0,
      letterSpacing: '0.025em',
    },
    tablet: {
      fontSize: '3rem',
      lineHeight: 1.0,
      letterSpacing: '0.025em',
    },
    desktop: {
      fontSize: '3.75rem',
      lineHeight: 1.0,
      letterSpacing: '0.025em',
    },
  },
  h2: {
    mobile: {
      fontSize: '1.5rem',
      lineHeight: 1.3,
    },
    tablet: {
      fontSize: '1.875rem',
      lineHeight: 1.3,
    },
    desktop: {
      fontSize: '2.25rem',
      lineHeight: 1.3,
    },
  },
  h3: {
    mobile: {
      fontSize: '1.125rem',
      lineHeight: 1.4,
      fontWeight: 600,
    },
    tablet: {
      fontSize: '1.25rem',
      lineHeight: 1.4,
      fontWeight: 600,
    },
    desktop: {
      fontSize: '1.5rem',
      lineHeight: 1.4,
      fontWeight: 600,
    },
  },
  body: {
    mobile: {
      fontSize: '0.875rem',
      lineHeight: 1.625,
    },
    desktop: {
      fontSize: '1rem',
      lineHeight: 1.625,
    },
  },
  bodyLarge: {
    mobile: {
      fontSize: '1rem',
      lineHeight: 1.625,
    },
    desktop: {
      fontSize: '1.125rem',
      lineHeight: 1.625,
    },
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    fontWeight: 400,
  },
  subhead: {
    mobile: {
      fontSize: '1.125rem',
      lineHeight: 1.625,
    },
    desktop: {
      fontSize: '1.25rem',
      lineHeight: 1.625,
    },
  },
  statsValue: {
    mobile: {
      fontSize: '1.5rem',
      fontWeight: 700,
    },
    desktop: {
      fontSize: '1.875rem',
      fontWeight: 700,
    },
  },
  statsLabel: {
    mobile: {
      fontSize: '0.75rem',
      fontWeight: 400,
    },
    desktop: {
      fontSize: '1rem',
      fontWeight: 400,
    },
  },
  badge: {
    fontSize: '0.875rem',
    fontWeight: 700,
    letterSpacing: '0.025em',
    textTransform: 'uppercase' as const,
  },
  badgeMobile: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.025em',
    textTransform: 'uppercase' as const,
  },
  cardTitle: {
    mobile: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    desktop: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
  },
  cardDescription: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.625,
  },
} as const;

export const V2_SPACING = {
  containerMaxWidth: '1280px',
  containerPadding: {
    mobile: '1rem',
    tablet: '1.5rem',
    desktop: '1.5rem',
  },
  sectionPadding: {
    small: {
      mobile: { top: '2rem', bottom: '2rem' },
      desktop: { top: '2rem', bottom: '2rem' },
    },
    medium: {
      mobile: { top: '2rem', bottom: '2rem' },
      desktop: { top: '2.5rem', bottom: '2.5rem' },
    },
    large: {
      mobile: { top: '2.5rem', bottom: '2.5rem' },
      desktop: { top: '5rem', bottom: '5rem' },
    },
    mobile: {
      top: '2.5rem',
      bottom: '0.5rem',
    },
    desktop: {
      top: '4rem',
      bottom: '1rem',
    },
  },
  cardBorderRadius: {
    mobile: '0.75rem',
    desktop: '0.75rem',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    '2xl': '1.5rem',
    '3xl': '1.5rem',
    pill: '1.125rem',
    full: '9999px',
  },
  buttonBorderRadius: '0.625rem',
  gridGap: {
    mobile: '2rem',
    desktop: '1.5rem',
  },
  contentSpacing: '1.5rem',
  statsGap: {
    mobile: '1.5rem',
    desktop: '3rem',
  },
  iconBox: {
    mobile: { width: '2.25rem', height: '2.25rem' },
    desktop: { width: '3rem', height: '3rem' },
  },
} as const;

export const V2_SHADOWS = {
  card: '0 2px 12px -2px hsla(168, 80%, 3%, 0.08)',
  cardHover: '0 8px 30px -6px hsla(168, 80%, 3%, 0.12)',
  premium: '0 4px 20px -4px hsla(187, 62%, 25%, 0.15)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
} as const;

export const V2_ANIMATIONS = {
  fadeIn: {
    keyframes: `
      @keyframes v2FadeIn {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
    css: 'v2FadeIn 0.5s ease-out forwards',
  },
  cardHover: {
    transform: 'translateY(-2px)',
    transition: 'all 0.3s ease-out',
  },
  scrollLogos: {
    keyframes: `
      @keyframes scrollLogos {
        from {
          transform: translateX(0);
        }
        to {
          transform: translateX(-50%);
        }
      }
    `,
    desktop: 'scrollLogos 25s linear infinite',
    mobile: 'scrollLogos 12s linear infinite',
  },
  pulse: {
    keyframes: `
      @keyframes v2Pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
    `,
    css: 'v2Pulse 2s ease-in-out infinite',
  },
} as const;

export const V2_BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

export const V2_MEDIA = {
  sm: '@media (min-width: 640px)',
  md: '@media (min-width: 768px)',
  lg: '@media (min-width: 1024px)',
  xl: '@media (min-width: 1280px)',
} as const;

export type V2ColorKey = keyof typeof V2_COLORS;
export type V2GradientKey = keyof typeof V2_GRADIENTS;
export type V2BreakpointKey = keyof typeof V2_BREAKPOINTS;
